import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ClinicHeader } from '../../../../../components/Reports/ClinicHeader';
import { ReportFooterTimestamp } from '../../../../../utils/reportUtils';
import useCurrency from '../../../../../components/CurrencyProvider';
import { PackageConsumptionReportResponse } from './consumptionDetails.helper';

import PatientDetailsForReport from '../../../../../components/Reports/PatientDetailsForReport';
import { usePatientContext } from '../../../../../components/patinetProvider';

// Common table cell styles
const headerCellStyle = {
  fontWeight: 600,
  color: '#4a5568',
  py: 0.75,
  px: 1,
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  borderBottom: '2px solid #e2e8f0',
  backgroundColor: '#f7fafc',
};

const bodyCellStyle = {
  py: 0.75,
  px: 1,
  fontSize: '0.75rem',
  color: '#2d3748',
  borderBottom: '1px solid #e2e8f0',
};

const totalRowStyle = {
  backgroundColor: '#f0fdf4',
  '& td': {
    borderBottom: 'none',
    fontWeight: 600,
  },
};

interface ConsumptionReportProps {
  reportData: PackageConsumptionReportResponse;
}

const ConsumptionReport: React.FC<ConsumptionReportProps> = ({ reportData }) => {
  const { t } = useTranslation('Packagebilling');
  const { formatCurrency } = useCurrency();
  const { patientId } = usePatientContext();
  const { package_info, services, pharmacy } = reportData;

  // Calculate totals
  const serviceTotals = services.reduce(
    (acc, item) => ({
      charges: acc.charges + item.charges,
      concession: acc.concession + item.concession_amount,
      discount: acc.discount + item.discount_amount,
      tax: acc.tax + item.tax_amount,
      net: acc.net + item.net_amount,
    }),
    { charges: 0, concession: 0, discount: 0, tax: 0, net: 0 }
  );

  const pharmacyTotals = pharmacy.reduce(
    (acc, item) => ({
      charges: acc.charges + (item.unit_price * item.billed_quantity),
      discount: acc.discount + item.discount_amount,
      tax: acc.tax + item.tax_amount,
      net: acc.net + item.net_amount,
    }),
    { charges: 0, discount: 0, tax: 0, net: 0 }
  );

  return (
    <Box
      data-testid="consumption-report"
      sx={{
        p: 2,
        pb: 5,
        backgroundColor: '#fff',
        color: '#2d3748',
        fontFamily: '"Inter", "Segoe UI", Arial, sans-serif',
        mx: 'auto',
        position: 'relative',
      }}
    >
      {/* Clinic Header */}
      <ClinicHeader />

      {/* Patient & Partner Info */}
      {/* <PatientDetailsHeader /> */}
      <PatientDetailsForReport patientId={patientId} />

      {/* Report Title */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          my: 1.5,
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            color: '#1a365d',
            px: 2,
            fontSize: '0.9rem',
          }}
        >
          {t('consumption_details.title')}
        </Typography>
        <Box sx={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
      </Box>

      {/* Package Info Card */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          border: '1px solid #e2e8f0',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        {/* Package Header */}
        <Box sx={{ 
          backgroundColor: '#f7fafc', 
          px: 1.5, 
          py: 0.5, 
          borderBottom: '1px solid #e2e8f0',
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#4a5568', 
              textTransform: 'uppercase', 
              letterSpacing: '0.5px',
              fontWeight: 600,
              fontSize: '0.65rem',
            }}
          >
            {t('consumption_details.report.package_information')}
          </Typography>
        </Box>
        
        {/* Package Details Grid - 15 items in 3 rows of 5 */}
        <Box sx={{ px: 1.5, py: 1 }}>
          {/* Row 1: Package Name & Code, Validity Period, Package Amount, Concession Amount, Net Amount */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: 1.5,
            mb: 1,
            pb: 1,
            borderBottom: '1px dashed #e2e8f0',
          }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.package_name')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a365d', fontSize: '0.8rem' }}>
                {package_info.name} (#{package_info.code})
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.validity_period')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#2d3748', fontSize: '0.8rem' }}>
                {package_info.start_date} - {package_info.valid_until}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.package_amount')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#805ad5', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.package_amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.concession_amount')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.concession_amount + package_info.total_service_concession_amount)}
              </Typography>
            </Box>
          </Box>

          {/* Row 2: Advance Paid, Advance Remaining, Advance Balance, Service Amount, Service Net Amount */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(6, 1fr)', 
            gap: 1.5,
            mb: 1,
            pb: 1,
            borderBottom: '1px dashed #e2e8f0',
          }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.net_amount')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.net_amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.advance_paid')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#38a169', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.advance_paid)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.advance_remaining')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#3182ce', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.net_amount - package_info.advance_paid)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: package_info.advance_balance_amount < 0 ? '#c53030' : '#dd6b20', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.advance_balance')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: package_info.advance_balance_amount < 0 ? '#c53030' : '#dd6b20', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.advance_balance_amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.service_amount')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#2d3748', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.service_amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.service_net_amount')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#2d3748', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.service_net_amount)}
              </Typography>
            </Box>
          </Box>

          {/* Row 3: Service Consumed, Service Pending, Pharmacy Amount, Pharmacy Consumed, Pharmacy Pending */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(5, 1fr)', 
            gap: 1.5,
          }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#38a169', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.service_consumed')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#38a169', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.service_consumed)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#dd6b20', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.service_pending')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#dd6b20', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.service_pending)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.pharmacy_amount')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#2d3748', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.pharmacy_amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#38a169', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.pharmacy_consumed')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#38a169', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.pharmacy_consumed)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#dd6b20', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 }}>
                {t('consumption_details.report.pharmacy_pending')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#dd6b20', fontSize: '0.8rem' }}>
                {formatCurrency(package_info.pharmacy_pending)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Services Table */}
      {services.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 0.5,
            gap: 0.75,
          }}>
            <Box sx={{ 
              width: 3, 
              height: 14, 
              backgroundColor: '#3182ce', 
              borderRadius: 0.5,
            }} />
            <Typography
              variant="caption"
              sx={{
                color: '#2d3748',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            >
              {t('consumption_details.report.services_consumed')}
            </Typography>
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: 35 }}>{t('consumption_details.report.sr_no')}</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 80 }}>{t('consumption_details.report.date')}</TableCell>
                  <TableCell sx={headerCellStyle}>{t('consumption_details.report.bill_number')}</TableCell>
                  <TableCell sx={headerCellStyle}>{t('consumption_details.report.service_name')}</TableCell>
                  <TableCell sx={headerCellStyle}>{t('consumption_details.report.prescribed_by')}</TableCell>
                  <TableCell sx={headerCellStyle} align="right">{t('consumption_details.report.charges')}</TableCell>
                  <TableCell sx={headerCellStyle} align="right">{t('consumption_details.report.discount')}</TableCell>
                  <TableCell sx={headerCellStyle} align="right">{t('consumption_details.report.net_amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((item, index) => (
                  <TableRow
                    key={`service-${item.sr_no}`}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#fff' : '#f7fafc',
                      '&:hover': { backgroundColor: '#edf2f7' },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellStyle, color: '#718096', fontWeight: 500 }}>{item.sr_no}</TableCell>
                    <TableCell sx={bodyCellStyle}>{item.date}</TableCell>
                    <TableCell sx={{ ...bodyCellStyle, color: '#718096' }}>{item.bill_number || '-'}</TableCell>
                    <TableCell sx={{ ...bodyCellStyle, fontWeight: 500, color: '#2d3748' }}>{item.service_name}</TableCell>
                    <TableCell sx={{ ...bodyCellStyle, color: '#4a5568' }}>{item.prescribed_by}</TableCell>
                    <TableCell align="right" sx={bodyCellStyle}>
                      {formatCurrency(item.charges)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellStyle, color: '#718096' }}>
                      {formatCurrency(item.discount_amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellStyle, fontWeight: 600, color: '#2d3748' }}>
                      {formatCurrency(item.net_amount)}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Services Totals Row */}
                <TableRow sx={totalRowStyle}>
                  <TableCell colSpan={5} sx={{ ...bodyCellStyle, fontWeight: 700, color: '#276749' }}>
                    {t('consumption_details.report.services_total')}
                  </TableCell>
                  <TableCell align="right" sx={{ ...bodyCellStyle, fontWeight: 700, color: '#276749' }}>
                    {formatCurrency(serviceTotals.charges)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...bodyCellStyle, fontWeight: 700, color: '#276749' }}>
                    {formatCurrency(serviceTotals.discount)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...bodyCellStyle, fontWeight: 700, color: '#276749' }}>
                    {formatCurrency(serviceTotals.net)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Pharmacy Table */}
      {pharmacy.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 0.5,
            gap: 0.75,
          }}>
            <Box sx={{ 
              width: 3, 
              height: 14, 
              backgroundColor: '#38a169', 
              borderRadius: 0.5,
            }} />
            <Typography
              variant="caption"
              sx={{
                color: '#2d3748',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            >
              {t('consumption_details.report.pharmacy_consumed_title')}
            </Typography>
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: 35 }}>{t('consumption_details.report.sr_no')}</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 80 }}>{t('consumption_details.report.date')}</TableCell>
                  <TableCell sx={headerCellStyle}>{t('consumption_details.report.bill_number')}</TableCell>
                  <TableCell sx={headerCellStyle}>{t('consumption_details.report.item_name')}</TableCell>
                  <TableCell sx={headerCellStyle} align="center">{t('consumption_details.report.qty')}</TableCell>
                  <TableCell sx={headerCellStyle} align="right">{t('consumption_details.report.unit_price')}</TableCell>
                  <TableCell sx={headerCellStyle} align="right">{t('consumption_details.report.discount')}</TableCell>
                  <TableCell sx={headerCellStyle} align="right">{t('consumption_details.report.net_amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pharmacy.map((item, index) => (
                  <TableRow
                    key={`pharmacy-${item.sr_no}`}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#fff' : '#f7fafc',
                      '&:hover': { backgroundColor: '#edf2f7' },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellStyle, color: '#718096', fontWeight: 500 }}>{item.sr_no}</TableCell>
                    <TableCell sx={bodyCellStyle}>{item.date}</TableCell>
                    <TableCell sx={{ ...bodyCellStyle, color: '#718096' }}>{item.bill_number || '-'}</TableCell>
                    <TableCell sx={{ ...bodyCellStyle, fontWeight: 500, color: '#2d3748' }}>{item.item_name}</TableCell>
                    <TableCell align="center" sx={{ ...bodyCellStyle, fontWeight: 600 }}>{item.billed_quantity}</TableCell>
                    <TableCell align="right" sx={bodyCellStyle}>
                      {formatCurrency(item.unit_price)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellStyle, color: '#718096' }}>
                      {formatCurrency(item.discount_amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellStyle, fontWeight: 600, color: '#2d3748' }}>
                      {formatCurrency(item.net_amount)}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Pharmacy Totals Row */}
                <TableRow sx={totalRowStyle}>
                  <TableCell colSpan={5} sx={{ ...bodyCellStyle, fontWeight: 700, color: '#276749' }}>
                    {t('consumption_details.report.pharmacy_total')}
                  </TableCell>
                  <TableCell align="right" sx={{ ...bodyCellStyle, fontWeight: 700, color: '#276749' }}>
                    {formatCurrency(pharmacyTotals.charges)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...bodyCellStyle, fontWeight: 700, color: '#276749' }}>
                    {formatCurrency(pharmacyTotals.discount)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...bodyCellStyle, fontWeight: 700, color: '#276749' }}>
                    {formatCurrency(pharmacyTotals.net)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Grand Total Section */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #c6f6d5',
          borderRadius: '4px',
          overflow: 'hidden',
          mb: 1,
        }}
      >
        {/* Header */}
        <Box sx={{ 
          backgroundColor: '#f0fff4', 
          px: 1.5, 
          py: 0.5, 
          borderBottom: '1px solid #c6f6d5',
        }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: '#276749', 
              textTransform: 'uppercase', 
              letterSpacing: '0.3px',
              fontWeight: 600,
              fontSize: '0.65rem',
            }}
          >
            {t('consumption_details.report.summary')}
          </Typography>
        </Box>
        
        {/* Summary Content */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            backgroundColor: '#f0fff4',
          }}
        >
          {/* Total Services */}
          <Box sx={{ 
            py: 0.75,
            px: 1,
            textAlign: 'center',
            borderRight: '1px solid #c6f6d5',
          }}>
            <Typography variant="caption" sx={{ color: '#4a5568', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600 }}>
              {t('consumption_details.report.total_services')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.85rem', lineHeight: 1.2 }}>
              {services.length}
            </Typography>
          </Box>
          
          {/* Total Pharmacy Items */}
          <Box sx={{ 
            py: 0.75,
            px: 1,
            textAlign: 'center',
            borderRight: '1px solid #c6f6d5',
          }}>
            <Typography variant="caption" sx={{ color: '#4a5568', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600 }}>
              {t('consumption_details.report.total_pharmacy_items')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.85rem', lineHeight: 1.2 }}>
              {pharmacy.length}
            </Typography>
          </Box>
          
          {/* Service Consumed */}
          <Box sx={{ 
            py: 0.75,
            px: 1,
            textAlign: 'center',
            borderRight: '1px solid #c6f6d5',
          }}>
            <Typography variant="caption" sx={{ color: '#38a169', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600 }}>
              {t('consumption_details.report.service_consumed')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#38a169', fontSize: '0.85rem', lineHeight: 1.2 }}>
              {formatCurrency(package_info.service_consumed)}
            </Typography>
          </Box>
          
          {/* Pharmacy Consumed */}
          <Box sx={{ 
            py: 0.75,
            px: 1,
            textAlign: 'center',
            borderRight: '1px solid #c6f6d5',
          }}>
            <Typography variant="caption" sx={{ color: '#38a169', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600 }}>
              {t('consumption_details.report.pharmacy_consumed')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#38a169', fontSize: '0.85rem', lineHeight: 1.2 }}>
              {formatCurrency(package_info.pharmacy_consumed)}
            </Typography>
          </Box>
          
          {/* Total Consumed */}
          <Box sx={{ 
            py: 0.75,
            px: 1,
            textAlign: 'center',
            backgroundColor: '#c6f6d5',
          }}>
            <Typography variant="caption" sx={{ color: '#276749', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600 }}>
              {t('consumption_details.report.total_consumed')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#276749', fontSize: '0.95rem', lineHeight: 1.2 }}>
              {formatCurrency(package_info.service_consumed + package_info.pharmacy_consumed)}
            </Typography>
          </Box>
        </Box>
      </Paper>
    
      <Divider />
      <ReportFooterTimestamp />
    </Box>
  );
};

export default ConsumptionReport;
